import { Dispatch, FC, SetStateAction } from 'react'

import { FormProvider, useForm } from 'react-hook-form'

import { LoadingContainer } from 'peach/components'
import { StyledButton } from 'peach/components/Button/Button'
import Input from 'peach/components/Input'

import { peachApi } from 'core/api'
import { R } from 'core/helpers'
import { Env, envStore, STANDARD_ENVIRONMENTS } from 'core/stores/env'
import {
  ResponseAPIKeyNoSecretAPIVersioning,
  useCompanyRoleGetAll,
  useInternalUserCreate,
} from 'core/types'

import {
  ErrorComponent,
  FormActions,
  Section,
  SectionLabel,
  Title,
} from './Components'
import { Company } from './types'

type CreateUserAndAPIKeyFormValues = {
  type?: 'borrower' | 'agent' | 'service'
  authType?: {
    email?: string
  }
  roleIds?: Array<string>
}

type CreateUserAndAPIKeyFormProps = {
  company: Company
  updateCompany: (obj: Company) => void
  title: string
  userType: 'lender' | 'peach'
  apiKeys?: Array<ResponseAPIKeyNoSecretAPIVersioning> | []
  setApiKeys: Dispatch<
    SetStateAction<Array<ResponseAPIKeyNoSecretAPIVersioning> | []>
  >
}

const CreateUserAndAPIKeyForm: FC<CreateUserAndAPIKeyFormProps> = ({
  company,
  updateCompany,
  title,
  userType,
  apiKeys,
  setApiKeys,
}) => {
  const { data: roles, isLoading: isLoadingRoles } = useCompanyRoleGetAll({
    companyId: company?.companyId,
  })

  const form = useForm<CreateUserAndAPIKeyFormValues>({
    values: {
      type: 'service',
      authType: {
        email: userType === 'peach' ? 'api@peach.finance' : '',
      },
      roleIds: [],
    },
  })

  const {
    mutateAsync: createUser,
    isPending,
    isError,
    error,
  } = useInternalUserCreate({
    companyId: company?.companyId,
  })

  const onCreate = async (values) => {
    try {
      const userResp = await createUser({
        type: values.type,
        authType: values.authType,
        roleIds: [
          R.pipe(
            roles ?? [],
            R.find((role) =>
              userType === 'peach'
                ? role?.alias === 'super-admin-role'
                : role?.alias === 'api',
            ),
          )?.id?.toString() ?? '',
        ],
      })

      // Get active environment from envStore
      const { activeEnvRef, customEnvs } = envStore.state
      let activeEnv: Env | undefined
      if (activeEnvRef) {
        if (activeEnvRef.type === 'standard') {
          const stdEnv = STANDARD_ENVIRONMENTS.find(
            (env) => env.id === activeEnvRef.id,
          )
          if (stdEnv) {
            activeEnv = { ...stdEnv, key: stdEnv.id, isStandard: true }
          }
        } else {
          activeEnv = customEnvs.find((env) => env.id === activeEnvRef.id)
        }
      }
      const apiBase = activeEnv?.apiBase || ''

      const apiKeyResp = await peachApi.post({
        url: `${apiBase}/companies/${company?.companyId}/keys`,
        body: {
          userId: userResp?.data?.id,
          apiVersion: import.meta.env.VITE_API_VERSION,
        },
      })

      if (apiKeyResp?.status !== 201) {
        throw new Error('Failed to create API key')
      }

      if (userResp?.status === 201 && userResp?.data) {
        updateCompany({
          ...company,
          users: [
            ...(company?.users ?? []),
            {
              userType,
              userId: userResp?.data?.userId?.toString(),
              id: userResp?.data?.id,
            },
          ],
        })
      }

      if (apiKeyResp?.status === 201 && apiKeyResp?.data) {
        setApiKeys([...(apiKeys ?? []), apiKeyResp?.data])
      }
    } catch (e) {
      console.error('Error with create actions: ', e)
      throw e
    }
  }

  return (
    <LoadingContainer loading={isPending || isLoadingRoles}>
      <Title>{title}</Title>
      <form
        onSubmit={form.handleSubmit(async (values) => {
          try {
            await onCreate(values)
          } catch (e) {
            console.error('Error with create actions: ', e)
          }
        })}
      >
        <FormProvider {...form}>
          <Input
            label='User Type'
            required
            type='text'
            {...form.register('type')}
          />

          <Section>
            <SectionLabel>Auth Type</SectionLabel>
            <Input
              label='Email'
              required
              type='text'
              {...form.register('authType.email')}
            />
          </Section>

          <Input
            disabled
            label='Role Alias'
            type='text'
            value={userType === 'peach' ? 'super-admin-role' : 'api-role'}
          />

          {isError &&
            !!error &&
            typeof error === 'object' &&
            'message' in error &&
            typeof error.message === 'string' && (
              <ErrorComponent>{error.message}</ErrorComponent>
            )}
          <FormActions>
            <StyledButton $primary type='submit'>
              Submit
            </StyledButton>
          </FormActions>
        </FormProvider>
      </form>
    </LoadingContainer>
  )
}

export default CreateUserAndAPIKeyForm
